import React, { useState, useEffect, useMemo } from 'react';
import useApiControl from '../../../common/useApiControl';
import BaseForm from '../BaseForm';
import { TextField, Autocomplete, MenuItem, Button, Box, Typography } from '@mui/material';
import FormFieldBlock from '../FormFieldBlock';
import CreateRoomConfirmDialog from '../../dialogs/CreateRoomConfirmDialog';
import { getBetDisplayLabel } from '../../room/betLabels';
import { isPositiveIntegerText } from '../../../common/plannedRoundCount';

const BONUS_OPTION_TIMING_RATE = 'timing_rate';
const RESULT_CONFIRMATION_METHOD = {
  MEMBER: 'member_confirmation',
  OWNER: 'owner_confirmation',
};
const BET_CLOSE_MODE = {
  TIMER: 'timer',
  MANUAL: 'manual',
};
const MIN_BET_CHOICES = 2;
const MAX_BET_CHOICES = 4;
const FIXED_ENTRY_ROOM_POINTS = 500;
const FIXED_MINIMUM_BET = 10;
const FIXED_MAXIMUM_BET = 100;
const MAX_BET_TIME_HOURS = 72;
const MAX_TIME_PART = 59;
const MAX_BET_TIME_SECONDS = (MAX_BET_TIME_HOURS * 60 * 60) + (MAX_TIME_PART * 60) + MAX_TIME_PART;
const EVENT_DESCRIPTION_MAX_LENGTH = 140;
const DEFAULT_EVENT_DESCRIPTION = '【結果の確認・判定】速報サイトや公式Xなど、結果を確認する情報源を入力\n【参考情報】開催日時、会場、視聴方法などを入力\n【補足・注意事項】中止・延期など、通常どおり判定できない場合の扱いを入力';
const RATE_CALCULATION_TYPE = {
  REALTIME: 'realtime',
  FINAL: 'final',
};
const RATE_CALCULATION_HELPER_TEXT = {
  [RATE_CALCULATION_TYPE.REALTIME]: 'ベットした時点のレートで確定します。「いつベットするか」も予想の判断材料にしたい場合におすすめです。',
  [RATE_CALCULATION_TYPE.FINAL]: 'ベット受付終了後にレートを確定します。ベットするタイミングによる有利・不利を抑えたい場合におすすめです。',
};
const BET_CLOSE_MODE_HELPER_TEXT = {
  [BET_CLOSE_MODE.TIMER]: '設定した受付時間が経過すると、自動でベット受付を終了します。試合を観戦しながら「開始後10分間」など、あらかじめ受付時間を決めて予想を楽しむ場合におすすめです。',
  [BET_CLOSE_MODE.MANUAL]: 'オーナーが結果登録を開始するまで、ベット受付を継続します。観戦しながら、展開に合わせて受付を終了したい予想におすすめです。',
};
const RESULT_CONFIRMATION_HELPER_TEXT = {
  [RESULT_CONFIRMATION_METHOD.MEMBER]: 'オーナーの結果登録後、参加者の確認を経て結果を確定します。判定に参加者の確認を取り入れたいルームにおすすめです。',
  [RESULT_CONFIRMATION_METHOD.OWNER]: 'オーナーが登録した結果を、そのまま確定結果とします。結果が明確で、テンポよく次のラウンドへ進めたいルームにおすすめです。',
};

const CreateForm = ({ user }) => {
  const [eventName, setEventName] = useState('');
  const [description, setDescription] = useState(DEFAULT_EVENT_DESCRIPTION);

  const [startMargin, setStartMargin] = useState('');
  const [betTimeHours, setBetTimeHours] = useState('0');
  const [betTimeMinutes, setBetTimeMinutes] = useState('0');
  const [betTimeSeconds, setBetTimeSeconds] = useState('0');
  const [plannedRoundCount, setPlannedRoundCount] = useState('1');

  const [rtype, setRoundType] = useState(0); //選択式以外の近似とかのパターンもやりたかった時の名残。0は選択式
  const [answers, setAnswers] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [isConfirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [tags, setTags] = useState('');
  const [tagsValue, setTagsValue] = useState([]);
  const [masterTagOptions, setMasterTagOptions] = useState([]);
  const [rateCalculationType, setRateCalculationType] = useState(RATE_CALCULATION_TYPE.REALTIME);
  const [resultConfirmationMethod, setResultConfirmationMethod] = useState(RESULT_CONFIRMATION_METHOD.MEMBER);
  const [betCloseMode, setBetCloseMode] = useState(BET_CLOSE_MODE.TIMER);
  const ownerCreateLimit = Number(user?.owner_create_limit ?? 0);
  // 時・分・秒入力の空欄は0秒扱いにして、既存APIのbettime秒パラメータへ合算する。
  const parseTimePart = (value) => {
    const parsed = value === '' ? 0 : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  // type="number"でも直接入力では範囲外値を持てるため、整数かつ上限内だけstateへ反映する。
  const updateTimePart = (setter, max) => (value) => {
    if (!/^\d*$/.test(value)) {
      return;
    }
    if (value !== '' && Number(value) > max) {
      return;
    }
    setter(value);
  };
  const betTime =
    (parseTimePart(betTimeHours) * 60 * 60) +
    (parseTimePart(betTimeMinutes) * 60) +
    parseTimePart(betTimeSeconds);

  useEffect(() => {
    if (rateCalculationType === RATE_CALCULATION_TYPE.FINAL) {
      setBetCloseMode(BET_CLOSE_MODE.TIMER);
    }
  }, [rateCalculationType]);

  const params1 = useMemo(() => ({
    name: eventName,
    description,
    rtype:rtype,
    answers:answers,
    entry_room_points: FIXED_ENTRY_ROOM_POINTS,
    planned_round_count: Number(plannedRoundCount),
    result_confirmation_method: resultConfirmationMethod,
    bet_close_mode: betCloseMode,
    minimum_bet: FIXED_MINIMUM_BET,
    max_bet: FIXED_MAXIMUM_BET,
    margin:Number(startMargin),
    bettime:Number(betTime),
    tags:tags,
    bonus_options: rateCalculationType === RATE_CALCULATION_TYPE.REALTIME ? [BONUS_OPTION_TIMING_RATE] : [],
  }), [eventName, description, rtype, answers, plannedRoundCount, resultConfirmationMethod, betCloseMode, startMargin, betTime, tags, rateCalculationType]);

  const { data: data1, error: error1, loading: loading1, executeRequest: executeRequest1 } = useApiControl({
    action: '/make_event',
    params: params1,
    method: 'POST',
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = {};

    if (answers.length < MIN_BET_CHOICES || answers.length > MAX_BET_CHOICES) {
      errors.answers = [`ベット対象は${MIN_BET_CHOICES}〜${MAX_BET_CHOICES}件で設定してください。`];
    }

    if (!isPositiveIntegerText(plannedRoundCount)) {
      errors.planned_round_count = ['予定ラウンド数は1以上の整数で入力してください。'];
    }

    if (betTime > MAX_BET_TIME_SECONDS) {
      errors.bettime = ['ベット受付時間は72時間59分59秒以内で入力してください。'];
    }

    if (Object.keys(errors).length > 0) {
      setValidationError({
        response: {
          status: 422,
          data: { errors },
        },
      });
      return;
    }

    setValidationError(null);
    setConfirmDialogVisible(true);
  };
  const handleConfirmSubmit = () => {
    setConfirmDialogVisible(false);
    executeRequest1();
  };

  // 作成できたら新しいオーナーコンソールへ
  useEffect(() => {
    if (data1) {
      // 作ったイベントの管理画面に移動
      window.location.href = '/owner-console/' + data1.room_id;
    }
  }, [data1]);

  // マスタータグ取得
  const { data: data2, loading: loading2, executeRequest: executeRequest2 } = useApiControl({
    action: '/get_master_tags',
    method: 'POST',
  });
  // マスタタグローカルストレージにあるかチェックしてなければAPI経由で取得
  useEffect(() => {
    // const localTags = localStorage.getItem('tags');
    // if (localTags) {
    //   try {
    //     const parsedTags = JSON.parse(localTags);
    //     setMasterTagOptions(
    //       parsedTags.map((tag, index) => ({
    //         label: tag.name,
    //         value: tag.id,
    //       }))
    //     );
    //   } catch (e) {
    //     console.error('ローカルストレージのtagsパースエラー', e);
    //     executeRequest2();
    //   }
    // } else {
      executeRequest2();
    // }
  }, []);
  
  useEffect(() => {
    if (data2 && data2.tags) {
      localStorage.setItem('tags', JSON.stringify(data2.tags));
      setMasterTagOptions(
        data2.tags.map((tag, index) => ({
          label: tag.name,
          value: tag.id,
        }))
      );
    }
  }, [data2]);
  
  useEffect(() => {
    if (data2 && data2.tags) {
      //ローカルストレージにキャッシュ
      localStorage.setItem('tags', JSON.stringify(data2.tags));
      //マスタ登録
      setMasterTagOptions(
        data2.tags.map((tag, index) => ({
          label: tag.name,
          value: tag.id,
        }))
      );
    }
  }, [data2]);

  // round.answersの制御ここから
  const [choices, setChoices] = useState(['', '']);


  // 選択肢の追加
  const addChoice = () => {
    setChoices((currentChoices) =>
      currentChoices.length >= MAX_BET_CHOICES
        ? currentChoices
        : [...currentChoices, '']
    );
  };
  // 選択肢の削除
  const removeChoice = (index) => {
    setChoices((currentChoices) =>
      currentChoices.length <= MIN_BET_CHOICES
        ? currentChoices
        : currentChoices.filter((_, i) => i !== index)
    );
  };
  // 選択肢の更新
  const updateChoice = (index, value) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
  };

  // 選択肢の配列形式への変換
  const getValidChoices = () => {
    return choices
      .slice(0, MAX_BET_CHOICES)
      .map((choice) => choice.trim())
      .filter((choice) => choice !== '');
  };

  // 選択肢をupdate_eventのパラメータにセット
  useEffect(() => {
    setAnswers(getValidChoices());
  }, [choices]);

  useEffect(() => {
    if (validationError && answers.length >= MIN_BET_CHOICES && answers.length <= MAX_BET_CHOICES) {
      setValidationError(null);
    }
  }, [answers, validationError]);

  // Errorを監視し、401エラーをキャッチする
  // useEffect(() => {

  // }, [error1]);


  const tagsHandleChange = (event, value) => {
    setTags(value.map((option) => option.value));
    setTagsValue(value);
  };

  return (
    <>
      <BaseForm
        onSubmit={handleSubmit}
        submitLabel="新規のベットページを作成"
        logMessage="create Api start"
        apiError={validationError || error1}
        disableEnterSubmit
      >
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #cbd5e1' }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 900 }}>
            所持アイテム
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            ルーム作成可能数：{ownerCreateLimit.toLocaleString()}回
          </Typography>
        </Box>
        <FormFieldBlock title="ベットルーム名" description="24文字まで入力できます。">
          <TextField
            label="ルーム名"
            variant="outlined"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            inputProps={{ maxLength: 24 }}
          />
        </FormFieldBlock>
        <FormFieldBlock title="レート計算の種類" description={RATE_CALCULATION_HELPER_TEXT[rateCalculationType]}>
          <TextField
            select
            label="選択してください"
            variant="outlined"
            value={rateCalculationType}
            onChange={(e) => setRateCalculationType(e.target.value)}
          >
            <MenuItem value={RATE_CALCULATION_TYPE.REALTIME}>リアルタイムレート</MenuItem>
            <MenuItem value={RATE_CALCULATION_TYPE.FINAL}>最終集計レート</MenuItem>
          </TextField>
        </FormFieldBlock>
      {rateCalculationType === RATE_CALCULATION_TYPE.REALTIME && (
        <FormFieldBlock title="ベット受付時間設定" description={BET_CLOSE_MODE_HELPER_TEXT[betCloseMode]}>
          <TextField
            select
            label="選択してください"
            value={betCloseMode}
            onChange={(e) => setBetCloseMode(e.target.value)}
          >
            <MenuItem value={BET_CLOSE_MODE.TIMER}>時間設定する</MenuItem>
            <MenuItem value={BET_CLOSE_MODE.MANUAL}>オーナーが受付を終了する</MenuItem>
          </TextField>
        </FormFieldBlock>
      )}
      {betCloseMode === BET_CLOSE_MODE.TIMER && (
        <FormFieldBlock title="ベット受付時間">
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextField
              label="時間(0～72)"
              type="number"
              value={betTimeHours}
              InputProps={{ inputProps: { min: 0, max: MAX_BET_TIME_HOURS, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeHours, MAX_BET_TIME_HOURS)(e.target.value)}
            />
            <TextField
              label="分(0～59)"
              type="number"
              value={betTimeMinutes}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeMinutes, MAX_TIME_PART)(e.target.value)}
            />
            <TextField
              label="秒(0～59)"
              type="number"
              value={betTimeSeconds}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeSeconds, MAX_TIME_PART)(e.target.value)}
            />
          </div>
        </FormFieldBlock>
      )}
      {rateCalculationType === RATE_CALCULATION_TYPE.REALTIME && betCloseMode === BET_CLOSE_MODE.MANUAL && (
        <FormFieldBlock title="レート変動の基準時間" description="どのくらいの時間でレートを変動させるかを設定できます。ベット締め切りの予定時間を入れることをおすすめします。">
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextField
              label="時間(0～72)"
              type="number"
              value={betTimeHours}
              InputProps={{ inputProps: { min: 0, max: MAX_BET_TIME_HOURS, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeHours, MAX_BET_TIME_HOURS)(e.target.value)}
            />
            <TextField
              label="分(0～59)"
              type="number"
              value={betTimeMinutes}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeMinutes, MAX_TIME_PART)(e.target.value)}
            />
            <TextField
              label="秒(0～59)"
              type="number"
              value={betTimeSeconds}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeSeconds, MAX_TIME_PART)(e.target.value)}
            />
          </div>
        </FormFieldBlock>
      )}
      <FormFieldBlock title="結果の確定方式" description={RESULT_CONFIRMATION_HELPER_TEXT[resultConfirmationMethod]}>
        <TextField
          select
          label="選択してください"
          variant="outlined"
          value={resultConfirmationMethod}
          onChange={(e) => setResultConfirmationMethod(e.target.value)}
        >
          <MenuItem value={RESULT_CONFIRMATION_METHOD.MEMBER}>メンバー確認方式</MenuItem>
          <MenuItem value={RESULT_CONFIRMATION_METHOD.OWNER}>オーナー確定方式</MenuItem>
        </TextField>
      </FormFieldBlock>
      <FormFieldBlock title="予定ラウンド数" description="参加者向け表示情報です。ラウンド単位で手動終了が行えます。">
        <TextField
          label="予定ラウンド数"
          type="number"
          variant="outlined"
          value={plannedRoundCount}
          onChange={(e) => setPlannedRoundCount(e.target.value)}
          InputProps={{ inputProps: { min: 1, step: 1 } }}
        />
      </FormFieldBlock>
      <FormFieldBlock title="補足情報" description={`${EVENT_DESCRIPTION_MAX_LENGTH}文字まで入力できます。`}>
        <TextField
          label="補足情報"
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={DEFAULT_EVENT_DESCRIPTION}
          multiline
          rows={3}
          inputProps={{ maxLength: EVENT_DESCRIPTION_MAX_LENGTH }}
        />
      </FormFieldBlock>
      
      
      {/*  
      <RadioGroup
        value={rtype}
        onChange={(e) => setRoundType(e.target.value)}
        label="パターン"
      >
        <FormControlLabel value="0" control={<Radio />} label="選択" />
        <FormControlLabel value="1" control={<Radio />} label="数当て" />
        <FormControlLabel value="" control={<Radio />} label="Other" /> 
      </RadioGroup>*/}

      <FormFieldBlock title="ベット対象の選択肢" description="2〜4件、各20文字まで入力できます。">
        {rtype === 0 && choices.map((choice, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label={getBetDisplayLabel(index)}
              variant="outlined"
              value={choice}
              onChange={(e) => updateChoice(index, e.target.value)}
              margin="normal"
              fullWidth
              inputProps={{ maxLength: 20 }}
            />
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              disabled={choices.length <= MIN_BET_CHOICES}
              onClick={() => removeChoice(index)}
              sx={{ flexShrink: 0, mt: 1, minWidth: 72 }}
            >
              削除
            </Button>
          </Box>
        ))}

        {rtype === 0 && (
          <Button
            type="button"
            variant="outlined"
            onClick={addChoice}
            disabled={choices.length >= MAX_BET_CHOICES}
            sx={{ alignSelf: 'flex-start' }}
          >
            選択肢を追加
          </Button>
        )}
      </FormFieldBlock>
      <FormFieldBlock title="ベット開始カウントダウン">
        <TextField
          label="秒(0～60)"
          type="number"
          value={startMargin}
          InputProps={{ inputProps: { min: 0, max: 60 } }}
          onChange={(e) => setStartMargin(e.target.value)}
        />
      </FormFieldBlock>
      {/*
      暫定対応: ルームチップ/ベット額は固定値でAPIへ送信する。
      入力欄を復活させる場合は state と validation も合わせて戻す。
      <FormFieldBlock title="ルームチップの参加時所持数" description="作成後は変更できません。">
        <TextField
          label="例: 500"
          type="number"
          value={entryRoomPoints}
          onChange={(e) => setEntryRoomPoints(e.target.value)}
          InputProps={{ inputProps: { min: 1, step: 1 } }}
        />
      </FormFieldBlock>

      <FormFieldBlock title="最小ベット額" description="作成後は変更できません。">
        <TextField
          label="例: 10"
          type="number"
          value={minimumBet}
          onChange={(e) => setMinimumBet(e.target.value)}
          InputProps={{ inputProps: { min: 1, step: 1 } }}
        />
      </FormFieldBlock>

      <FormFieldBlock title="最大ベット額" description="作成後は変更できません。">
        <TextField
          label="例: 100"
          type="number"
          value={maximumBet}
          onChange={(e) => setMaximumBet(e.target.value)}
          InputProps={{ inputProps: { min: 1, step: 1 } }}
        />
      </FormFieldBlock>
      */}

      <FormFieldBlock title="ルームのカテゴリ">
          <Autocomplete
            multiple
            options={masterTagOptions}
            value={tagsValue}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} label="複数選択" variant="outlined" />
            )}
            onChange={tagsHandleChange}
          />
        </FormFieldBlock>
      </BaseForm>
      <CreateRoomConfirmDialog
        isVisible={isConfirmDialogVisible}
        onClose={() => setConfirmDialogVisible(false)}
        onConfirm={handleConfirmSubmit}
        ownerCreateLimit={ownerCreateLimit}
        loading={loading1}
      />
    </>
  );
};

export default CreateForm;
